import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { AppConfigService } from '~/core/services/appconfig.service';
import { MsalService } from '@azure/msal-angular';

export interface ConversationHistoryItem {
    type?: string;
    timestamp: string;
    conversation_id?: string;
    user_input: string;
    ai_response: string | Record<string, any>;
    response?: string;
    agent_used?: string;
    primary_agent?: string;
    secondary_agents?: string[];
    confidence_score?: number;
    confidence_reasoning?: string;
    citations?: { [key: string]: string };
    routing?: {
        primary_agent?: string;
        secondary_agents?: string[];
        confidence?: number;
        reasoning?: string;
    };
    feedback?: {
        sentiment?: 'like' | 'dislike' | string;
    };
}

export interface ThreadHistorySummary {
    threadId: string;
    chatTitle: string;
    lastActive?: string;
}

export interface ThreadSummaryEntry {
    chat_title?: string;
    last_active?: string;
}

export interface ThreadSummariesResponse {
    status: string;
    threads: Record<string, ThreadSummaryEntry>;
}

export interface ThreadConversationResponse {
    status: string;
    chat_title?: string;
    conversations: ConversationHistoryItem[];
}

/**
 * Conversation History Service
 * Handles fetching and managing conversation history from backend
 */
@Injectable({
    providedIn: 'root'
})
export class ConversationHistoryService {
    private apiBaseUrl: string = '';
    private readonly THREADS_ENDPOINT = '/conversation/threads';
    private threadSummariesSubject = new BehaviorSubject<ThreadSummariesResponse | null>(null);
    private threadConversationsCache = new Map<string, ThreadConversationResponse>();
    public conversationHistory$ = this.threadSummariesSubject.asObservable();

    constructor(
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private authService: MsalService
    ) {
        this.loadConfigUrl();
    }

    /**
     * Load API base URL from config.json
     */
    private loadConfigUrl(): void {
        this.apiBaseUrl = this.appConfigService.getConfig('apiUrl');
    }

    /**
     * Get JWT token from MSAL
     */
    private getJwtToken(): string {
        try {
            const account = this.authService.instance.getActiveAccount();
            if (!account) {
                console.error('❌ [CONVERSATION-HISTORY] No active MSAL account found');
                return '';
            }
            return account.idToken || '';
        } catch (error) {
            console.error('❌ [CONVERSATION-HISTORY] Error getting JWT token:', error);
            return '';
        }
    }

    /**
     * Fetch conversation history for the authenticated user
     */
    public fetchThreadSummaries(): Observable<ThreadSummariesResponse> {
        const jwtToken = this.getJwtToken();
        
        if (!jwtToken) {
            console.error('❌ [CONVERSATION-HISTORY] JWT token not available');
            throw new Error('Authentication token not available');
        }

        const headers = new HttpHeaders({
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json'
        });

        const url = `${this.apiBaseUrl}${this.THREADS_ENDPOINT}`;
        
        return new Observable((observer) => {
            this.http.get<ThreadSummariesResponse>(url, { headers }).subscribe({
                next: (data: ThreadSummariesResponse) => {
                    console.log('✅ [CONVERSATION-HISTORY] Fetched thread summaries', data);
                    this.threadSummariesSubject.next(data);
                    observer.next(data);
                    observer.complete();
                },
                error: (error) => {
                    console.error('❌ [CONVERSATION-HISTORY] Error fetching thread summaries:', error);
                    observer.error(error);
                }
            });
        });
    }

    /**
     * Backward-compatible alias used by existing callers.
     */
    public fetchConversationHistory(): Observable<ThreadSummariesResponse> {
        return this.fetchThreadSummaries();
    }

    /**
     * Fetch conversation history for a specific thread ID
     */
    public fetchThreadConversation(threadId: string): Observable<ThreadConversationResponse> {
        const jwtToken = this.getJwtToken();

        if (!jwtToken) {
            console.error('❌ [CONVERSATION-HISTORY] JWT token not available');
            throw new Error('Authentication token not available');
        }

        const sanitizedThreadId = (threadId || '').trim();
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json'
        });
        const url = `${this.apiBaseUrl}${this.THREADS_ENDPOINT}/${encodeURIComponent(sanitizedThreadId)}`;

        return new Observable((observer) => {
            this.http.get<ThreadConversationResponse>(url, { headers }).subscribe({
                next: (data: ThreadConversationResponse) => {
                    console.log('✅ [CONVERSATION-HISTORY] Fetched thread conversation', { threadId: sanitizedThreadId });
                    this.threadConversationsCache.set(sanitizedThreadId, {
                        status: data?.status || 'success',
                        chat_title: data?.chat_title,
                        conversations: Array.isArray(data?.conversations) ? data.conversations : []
                    });
                    observer.next(data);
                    observer.complete();
                },
                error: (error) => {
                    console.error('❌ [CONVERSATION-HISTORY] Error fetching thread conversation:', error);
                    observer.error(error);
                }
            });
        });
    }

    /**
     * Get stored conversation history from subject
     */
    public getConversationHistory(): ThreadSummariesResponse | null {
        return this.threadSummariesSubject.value;
    }

    /**
     * Get list of thread IDs
     */
    public getThreadIds(): string[] {
        const history = this.threadSummariesSubject.value;
        if (!history || !history.threads) {
            return [];
        }
        return Object.keys(history.threads);
    }

    /**
     * Get thread summaries for sidebar display
     */
    public getThreadSummaries(): ThreadHistorySummary[] {
        const history = this.threadSummariesSubject.value;
        if (!history || !history.threads) {
            return [];
        }

        return Object.entries(history.threads).map(([threadId, thread]) => ({
            threadId,
            chatTitle: (thread?.chat_title || '').trim() || threadId,
            lastActive: thread?.last_active
        }));
    }

    /**
     * Get messages for a specific thread
     */
    public getThreadMessages(threadId: string): ConversationHistoryItem[] {
        const cacheKey = (threadId || '').trim();
        if (!cacheKey || !this.threadConversationsCache.has(cacheKey)) {
            return [];
        }

        const thread = this.threadConversationsCache.get(cacheKey);
        return Array.isArray(thread?.conversations) ? thread.conversations : [];
    }

    /**
     * Returns true if a thread's conversation payload has been fetched and cached.
     */
    public hasThreadConversation(threadId: string): boolean {
        const cacheKey = (threadId || '').trim();
        return !!cacheKey && this.threadConversationsCache.has(cacheKey);
    }

    /**
     * Clear cached conversation history
     */
    public clearCache(): void {
        this.threadSummariesSubject.next(null);
        this.threadConversationsCache.clear();
    }
}
